import { ApplicationElement, html } from '@effectivastudio/lit-components';
import '@effectivastudio/lit-components/components/icon/element.js';
import '@effectivastudio/lit-components/components/list_item_10/element.js';
import translations from './translations.js';
import style from './style.lit.css';

/**
 * Displays the event date (or start and end)
 *
 * @prop from - Start date of the event
 * @prop to - End date of the event
 * @prop onlyDate - Should omit hours and minutes?
 *
 * @cssprop [--icon-border-radius: 6px] Border radius of ef-icon
 * @cssprop [--icon-size: 50px] Width and height of ef-icon
 */

export default class EventDate extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    from: { type: Date },
    to: { type: Date },
    onlyDate: { type: Boolean },
  };

  /** @internal */
  static translations = translations;

  /** @internal */
  get sameYear() {
    return this.from.getFullYear() === this.to.getFullYear();
  }

  /** @internal */
  get sameMonth() {
    return this.from.getMonth() === this.to.getMonth();
  }

  /** @internal */
  get sameDay() {
    return this.from.getDate() === this.to.getDate();
  }

  /** @internal */
  get oneDayEvent() {
    return this.sameYear && this.sameMonth && this.sameDay;
  }

  /** @internal */
  get timeRange() {
    return `${this.timeOnly(this.from)} - ${this.timeOnly(this.to)}`;
  }

  timeOnly(date) {
    return this.i18n.l('time.formats.time', date);
  }

  renderOneDayEvent() {
    return html`
      <div>
        <strong>${this.i18n.l('date.formats.short', this.from)}</strong>
      </div>
      <div>
        ${this.onlyDate
          ? this.i18n.translations[this.i18n.locale].date.day_names[
              this.from.getDay()
            ]
          : this.timeRange}
      </div>
    `;
  }

  renderManyDaysEvent() {
    const format = this.onlyDate ? 'date.formats.short' : 'time.formats.short';

    return html`
      <div>
        <strong>${this.t('start')}:</strong>
        ${this.i18n.l(format, this.from)}
      </div>
      <div>
        <strong>${this.t('end')}:</strong>
        ${this.i18n.l(format, this.to)}
      </div>
    `;
  }

  render() {
    return html`
      <ef-list-item-10>
        <ef-icon name="calendar-day" slot="left"></ef-icon>
        <main slot="main">
          ${this.oneDayEvent
            ? this.renderOneDayEvent()
            : this.renderManyDaysEvent()}
        </main>
      </ef-list-item-10>
    `;
  }
}

EventDate.registerTagName('xs-event-date');

customElements.get('xs-event-date') ||
  customElements.define('xs-event-date', EventDate);
